import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FaGripVertical } from 'react-icons/fa';
import { Button } from 'primereact/button';

import { useNewCreatePlan } from '../../hooks/coach/useNewCreatePlan';
import { useDraftWorkoutsStore, WorkoutDraftSource } from '../../stores/useDraftWorkoutsStore';
import { useToast } from '../../contexts/ToastContext';
import { CreatePlanHeader } from '../../components/plans/CreatePlanHeader';
import { useDragAndDrop } from '../../hooks/useDragAndDrop';
import { useSetConfigDialog } from '../../hooks/dialogs/useSetConfigDialog';
import { DragableRow } from '../../components/shared/DragableRow';
import { ExerciseDropdown } from '../../components/shared/ExerciseDropdown';
import { SetConfigDialog } from '../../dialogs/SetConfigDialog';
import ImportWorkoutAIDialog from '../../components/dialogs/ImportWorkoutAIDialog';
import QuickCreateExerciseDialog from '../../components/dialogs/QuickCreateExerciseDialog';
import { IPlanGroup, IPlanExercise } from '../../types/workout/plan-state';
import { computeUsedOptionalColumns } from '../../components/plans/blockAdaptiveColumns';

interface LocationState {
  planId?: number;
  isTemplate?: boolean;
  isEdit?: boolean;
  clientId?: number;
  sessionDate?: string;
  changeToTemplate?: boolean;
  returnTo?: string;
}

export const NewCreatePlan: React.FC = () => {
  const intl = useIntl();
  const { planId: planIdParam } = useParams<{ planId?: string }>();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const pathname = useLocation().pathname;
  const isEdit = pathname.includes('/edit/') || pathname.includes('/view/') || state?.isEdit === true;
  const isViewOnly = pathname.includes('/view/');
  const parsedParamId = planIdParam ? Number(planIdParam) : undefined;
  const paramPlanId = Number.isNaN(parsedParamId) ? undefined : parsedParamId;
  const statePlanId = state?.planId ? Number(state.planId) : undefined;
  const planId = !Number.isNaN(paramPlanId) && paramPlanId !== undefined ? paramPlanId : statePlanId;

  // When navigating from "add training to this day", changeToTemplate is false and clientId/sessionDate are set
  const hasAssignmentContext = state?.clientId !== undefined && state?.sessionDate !== undefined;
  const isTemplate = hasAssignmentContext ? (state?.changeToTemplate ?? false) : (state?.isTemplate ?? true);
  const clientId = state?.clientId?.toString();
  const sessionDate = state?.sessionDate;

  const {
    plan,
    groups,
    exercises,
    isSaving,
    updatePlanName,
    updateInstanceName,
    updateExerciseSelection,
    updateExerciseProperty,
    getExerciseKey,
    selectedGroup,
    selectedExercise,
    setSelectedGroup,
    setSelectedExercise,
    updateSetConfiguration,
    toggleSetConfiguration,

    addGroup,
    removeGroup,
    addExerciseToGroup,
    removeExerciseFromGroup,
    addExerciseAtPosition,
    hoverRowIndex,
    showInsertButton,
    isInsertButtonHovered,
    insertPosition,
    setHoverRowIndex,
    setShowInsertButton,
    setIsInsertButtonHovered,
    setInsertPosition,
    setGroups,
    handleSavePlan,

    editingGroupName,
    setEditingGroupName,
    updateGroupName,
    handleGoBack,
    isViewOnly: isViewOnlyMode,
    setPlan
  } = useNewCreatePlan({
    planId,
    isTemplate,
    isEdit,
    isViewOnly,
    clientId,
    sessionDate
  });

  const [isEditing, setIsEditing] = useState(false);
  const [importDialogVisible, setImportDialogVisible] = useState(false);
  const [quickCreate, setQuickCreate] = useState<{
    visible: boolean;
    initialName: string;
    target: { groupId: string | number; exerciseId: string | number } | null;
  }>({ visible: false, initialName: '', target: null });

  // --- Drafts (localStorage, max 3) ---
  const { showToast } = useToast();
  // Selector de referencia estable + memo para filter por kind. Evita
  // infinite loop por crear array nuevo en cada render.
  const allDrafts = useDraftWorkoutsStore((s) => s.drafts);
  const drafts = useMemo(() => allDrafts.filter((d) => d.kind === 'workout'), [allDrafts]);
  const saveDraftAction = useDraftWorkoutsStore((s) => s.saveDraft);
  const deleteDraftAction = useDraftWorkoutsStore((s) => s.deleteDraft);
  const loadDraftAction = useDraftWorkoutsStore((s) => s.loadDraft);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [draftSource, setDraftSource] = useState<WorkoutDraftSource | null>(null);
  // No queremos auto-save durante el primer render mientras se carga el plan.
  // El flag se prende cuando el coach genera contenido nuevo (import IA o save manual).
  const autoSaveEnabledRef = useRef(false);

  // Auto-save debounced (500ms). Solo activo cuando hay un draft "en curso":
  // - importado por IA (auto-save desde el parse)
  // - guardado manualmente al menos una vez
  useEffect(() => {
    if (!autoSaveEnabledRef.current || !currentDraftId || !draftSource) return;
    if (isViewOnlyMode || isEdit) return; // no auto-save en edicion de un plan ya persistido
    const handle = setTimeout(() => {
      saveDraftAction({
        kind: 'workout',
        id: currentDraftId,
        planName: plan.planName ?? '',
        instanceName: plan.instanceName ?? '',
        personalizedNotes: plan.personalizedNotes ?? '',
        groups,
        source: draftSource
      });
    }, 500);
    return () => clearTimeout(handle);
  }, [
    groups,
    plan.planName,
    plan.instanceName,
    plan.personalizedNotes,
    currentDraftId,
    draftSource,
    saveDraftAction,
    isViewOnlyMode,
    isEdit
  ]);

  const handleManualSaveDraft = () => {
    const planName = plan.planName?.trim() || plan.instanceName?.trim();
    if (!planName) {
      showToast('warn', 'Nombre requerido', 'Asigna un nombre al plan antes de guardar el borrador');
      return;
    }
    const id = saveDraftAction({
      kind: 'workout',
      id: currentDraftId ?? undefined,
      planName: plan.planName ?? '',
      instanceName: plan.instanceName ?? '',
      personalizedNotes: plan.personalizedNotes ?? '',
      groups,
      source: draftSource ?? 'manual'
    });
    setCurrentDraftId(id);
    if (!draftSource) setDraftSource('manual');
    autoSaveEnabledRef.current = true;
    showToast(
      'success',
      intl.formatMessage({ id: 'coach.drafts.savedToast' }),
      intl.formatMessage({ id: 'coach.drafts.savedDetail' })
    );
  };

  const handleLoadDraft = (id: string) => {
    const d = loadDraftAction(id);
    if (!d || d.kind !== 'workout') return;
    setPlan((prev) => ({
      ...prev,
      planName: d.planName,
      instanceName: d.instanceName,
      personalizedNotes: d.personalizedNotes
    }));
    setGroups(d.groups);
    setCurrentDraftId(d.id);
    setDraftSource(d.source);
    autoSaveEnabledRef.current = true;
    showToast('info', intl.formatMessage({ id: 'coach.drafts.loadedToast' }), d.planName || '');
  };

  const handleDeleteDraft = (id: string) => {
    deleteDraftAction(id);
    if (id === currentDraftId) {
      setCurrentDraftId(null);
      setDraftSource(null);
      autoSaveEnabledRef.current = false;
    }
    showToast('info', intl.formatMessage({ id: 'coach.drafts.deletedToast' }), '');
  };

  const handleNewBlankWorkout = () => {
    setPlan((prev) => ({ ...prev, planName: '', instanceName: '', personalizedNotes: '' }));
    setGroups([]);
    setCurrentDraftId(null);
    setDraftSource(null);
    autoSaveEnabledRef.current = false;
  };

  const { visible, openDialog, closeDialog } = useSetConfigDialog();

  const dragAndDrop = useDragAndDrop(groups, setGroups) as {
    sensors: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    handleDragStart: (event: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
    handleDragEnd: (event: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
    handleDragOver: (event: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
    activeId: string | null;
    activeGroup: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    customCollisionDetection: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };

  const { sensors, handleDragStart, handleDragEnd, handleDragOver, activeId, activeGroup, customCollisionDetection } =
    dragAndDrop;

  const [overId, setOverId] = useState<string | null>(null);

  const activeExercise = useMemo(() => {
    if (!activeId || !activeId.startsWith('exercise-')) return null;
    const activeKey = activeId.replace('exercise-', '');
    for (const group of groups) {
      const exercise = group.exercises.find((ex) => getExerciseKey(group.id, ex) === activeKey);
      if (exercise) return exercise;
    }
    return null;
  }, [activeId, groups, getExerciseKey]);

  const handleOpenSetConfigDialog = (group: IPlanGroup, exercise: IPlanExercise) => {
    setSelectedGroup(group);
    setSelectedExercise(exercise);
    openDialog();
  };

  const sumExercises = (gs: typeof groups) => gs.reduce((acc, g) => acc + g.exercises.length, 0);
  const totalExercises = useMemo(() => sumExercises(groups), [groups]);

  return (
    <>
      <CreatePlanHeader
        planName={isTemplate ? plan.planName || '' : plan.instanceName || ''}
        onPlanNameChange={(v) => (isTemplate ? updatePlanName(v) : updateInstanceName(v))}
        onBack={handleGoBack}
        onCancel={handleGoBack}
        onSave={handleSavePlan}
        isSaving={isSaving}
        isViewOnlyMode={isViewOnlyMode}
        onAddGroup={addGroup}
        onAddExercise={() => setQuickCreate({ visible: true, initialName: '', target: null })}
        onImportAI={() => setImportDialogVisible(true)}
        onSaveDraft={handleManualSaveDraft}
        drafts={drafts}
        currentDraftId={currentDraftId}
        onLoadDraft={handleLoadDraft}
        onDeleteDraft={handleDeleteDraft}
        onNewBlank={handleNewBlankWorkout}
        totalGroups={groups.length}
        totalExercises={totalExercises}
      />

      {/* Groups */}
      <div
        style={{
          padding: '0 0.75rem 2rem',
          maxWidth: '900px',
          margin: '0 auto'
        }}
      >
        {groups.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragEnd={(event) => {
              setOverId(null);
              handleDragEnd(event);
            }}
            onDragStart={handleDragStart}
            onDragOver={(event) => {
              setOverId(event.over?.id?.toString() || null);
              handleDragOver(event);
            }}
          >
            <SortableContext items={groups.map((group) => `group-${group.id}`)} strategy={verticalListSortingStrategy}>
              {groups.map((group, groupIndex) => (
                <DragableRow key={group.id} id={`group-${group.id}`} data={group} type="group" disabled={isEditing}>
                  <div
                    style={{
                      marginBottom: '0.75rem',
                      background: 'var(--ios-card-bg)',
                      borderRadius: 'var(--ios-radius-lg)',
                      border: '1px solid var(--ios-card-border)',
                      boxShadow: 'var(--ios-card-shadow)',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Group Header */}
                    <div
                      className="flex align-items-center"
                      style={{
                        padding: '0.6rem 0.75rem',
                        background: '#a3e635',
                        color: '#0a0a0a',
                        gap: '0.5rem'
                      }}
                    >
                      <FaGripVertical style={{ cursor: 'grab', flexShrink: 0 }} />
                      {editingGroupName !== group.groupNumber ? (
                        <div
                          className="font-bold flex-grow-1 flex align-items-center gap-1"
                          style={{
                            cursor: 'pointer',
                            fontSize: '0.88rem',
                            opacity: 0.9
                          }}
                          onClick={() => setEditingGroupName(group.groupNumber)}
                          title="Click para renombrar"
                        >
                          {group.name ? group.name : `Grupo ${group.groupNumber}`}
                          <i className="pi pi-pencil" style={{ fontSize: '0.65rem', opacity: 0.6 }} />
                        </div>
                      ) : (
                        <div className="flex-grow-1" style={{ position: 'relative' }}>
                          <InputText
                            className="p-inputtext-sm w-full"
                            value={group.name || ''}
                            onChange={(e) => updateGroupName(group.groupNumber, e.target.value)}
                            onFocus={() => setEditingGroupName(group.groupNumber)}
                            onBlur={() => setEditingGroupName(null)}
                            onKeyDown={(e) => e.stopPropagation()}
                            maxLength={255}
                            placeholder="Nombre del grupo"
                            style={{
                              background: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              color: '#fff',
                              borderRadius: 'var(--ios-radius-sm)'
                            }}
                          />
                          <small
                            style={{
                              position: 'absolute',
                              right: '0.5rem',
                              bottom: '-1rem',
                              color: 'rgba(255,255,255,0.5)',
                              fontSize: '0.6rem'
                            }}
                          >
                            {(group.name || '').length}/255
                          </small>
                        </div>
                      )}
                      <Button
                        icon="pi pi-plus"
                        className="p-button-sm p-button-rounded p-button-text"
                        onClick={() => addExerciseToGroup(group.id)}
                        tooltip="Agregar ejercicio"
                        style={{
                          color: 'white',
                          width: '1.75rem',
                          height: '1.75rem',
                          flexShrink: 0
                        }}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-sm p-button-rounded p-button-text"
                        onClick={() => removeGroup(group.id)}
                        tooltip="Eliminar grupo"
                        style={{
                          color: 'rgba(255,255,255,0.8)',
                          width: '1.75rem',
                          height: '1.75rem',
                          flexShrink: 0
                        }}
                      />
                    </div>

                    {/* v2 adaptive columns pill — surfaces when a block has
                        per-set overrides hidden behind the Set Config dialog */}
                    {(() => {
                      const usedExtra = computeUsedOptionalColumns(group);
                      if (usedExtra.length === 0) return null;
                      return (
                        <div
                          className="px-3 py-2 flex align-items-center gap-2"
                          style={{
                            background: 'var(--et-primary-soft)',
                            borderBottom: '1px solid var(--ios-divider)',
                            fontSize: '0.72rem',
                            color: 'var(--et-primary-ink)',
                            fontWeight: 600
                          }}
                          title={usedExtra.join(' • ')}
                        >
                          <i className="pi pi-sliders-h" style={{ fontSize: '0.8rem' }} />
                          <span>
                            {intl.formatMessage({ id: 'planEditor.adaptive.extraColumns' }, { n: usedExtra.length })}
                          </span>
                          <span style={{ opacity: 0.7, fontWeight: 500 }}>· {usedExtra.join(' • ')}</span>
                        </div>
                      );
                    })()}

                    {/* GroupType selector oculto en web — solo se usa desde mobile.
                        En web los grupos quedan siempre STANDARD (default del state). */}

                    {/* Exercise Table - horizontal scroll on mobile */}
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ minWidth: '720px' }}>
                        {/* Table Header */}
                        <div
                          className="flex align-items-center"
                          style={{
                            background: 'var(--ios-surface-subtle)',
                            borderBottom: '1px solid var(--ios-divider)',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: 'var(--ios-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em'
                          }}
                        >
                          <div style={{ width: '40px' }}></div>
                          <div style={{ width: '300px' }} className="p-2 border-right-1 surface-border">
                            Ejercicio
                          </div>
                          <div style={{ width: '80px' }} className="p-2 border-right-1 surface-border text-center">
                            Series
                          </div>
                          <div style={{ width: '80px' }} className="p-2 border-right-1 surface-border text-center">
                            Reps
                          </div>
                          <div style={{ width: '80px' }} className="p-2 border-right-1 surface-border text-center">
                            Peso
                          </div>
                          <div style={{ width: '80px' }} className="p-2 border-right-1 surface-border text-center">
                            Duración
                          </div>
                          <div style={{ width: '60px' }} className="p-2 text-center">
                            Opc
                          </div>
                        </div>

                        {/* Exercise Rows */}
                        <SortableContext
                          items={group.exercises.map((exercise) => `exercise-${getExerciseKey(group.id, exercise)}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {group.exercises.map((exercise, exerciseIndex) => {
                            const exerciseKey = getExerciseKey(group.id, exercise);
                            const exerciseId = `exercise-${exerciseKey}`;
                            const globalIndex = groupIndex + exerciseIndex + 1;
                            const isDragOverExercise =
                              activeId &&
                              activeId.startsWith('exercise-') &&
                              overId === exerciseId &&
                              group.exercises.length === 0;

                            return (
                              <DragableRow
                                key={exerciseKey}
                                id={exerciseId}
                                data={exercise}
                                type="exercise"
                                disabled={isEditing}
                              >
                                {isDragOverExercise && (
                                  <div
                                    style={{
                                      height: '2px',
                                      background: '#a3e635',
                                      margin: '2px 0',
                                      borderRadius: '2px'
                                    }}
                                  />
                                )}
                                <div
                                  className="flex align-items-center"
                                  style={{
                                    borderBottom: '1px solid var(--ios-divider)',
                                    transition: 'background 0.15s ease'
                                  }}
                                >
                                  {/* Drag handle + insert */}
                                  <div
                                    style={{
                                      width: '40px',
                                      height: '44px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      position: 'relative',
                                      cursor: 'grab'
                                    }}
                                    onMouseMove={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const y = e.clientY - rect.top;
                                      if (y < rect.height / 2 && exerciseIndex > 0) {
                                        setInsertPosition('above');
                                      } else {
                                        setInsertPosition('below');
                                      }
                                      setHoverRowIndex(globalIndex);
                                      setShowInsertButton(true);
                                    }}
                                    onMouseLeave={() => {
                                      setTimeout(() => {
                                        if (!isInsertButtonHovered) {
                                          setHoverRowIndex(null);
                                          setShowInsertButton(false);
                                        }
                                      }, 50);
                                    }}
                                  >
                                    <FaGripVertical
                                      style={{
                                        color: 'var(--ios-text-tertiary)',
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                    {showInsertButton && hoverRowIndex === globalIndex && (
                                      <Button
                                        icon="pi pi-plus"
                                        className="p-button-rounded p-button-sm"
                                        onMouseEnter={() => setIsInsertButtonHovered(true)}
                                        onMouseLeave={() => {
                                          setIsInsertButtonHovered(false);
                                          setHoverRowIndex(null);
                                          setShowInsertButton(false);
                                        }}
                                        onClick={() =>
                                          addExerciseAtPosition(groupIndex, exerciseIndex, insertPosition === 'above')
                                        }
                                        style={{
                                          position: 'absolute',
                                          left: '-10px',
                                          [insertPosition === 'above' ? 'top' : 'bottom']: '-12px',
                                          width: '22px',
                                          height: '22px',
                                          padding: 0,
                                          minWidth: '22px',
                                          background: '#a3e635',
                                          border: 'none'
                                        }}
                                        tooltip="Insertar ejercicio"
                                      />
                                    )}
                                  </div>

                                  {/* Exercise Dropdown */}
                                  <div style={{ width: '300px' }} className="p-2 border-right-1 surface-border">
                                    <ExerciseDropdown
                                      exercises={exercises}
                                      value={exercise.exercise?.id ?? null}
                                      onChange={(val) => updateExerciseSelection(group.id, exercise.id, val)}
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={() => setIsEditing(false)}
                                      onCreateNew={(query) =>
                                        setQuickCreate({
                                          visible: true,
                                          initialName: query,
                                          target: { groupId: group.id, exerciseId: exercise.id }
                                        })
                                      }
                                    />
                                  </div>

                                  {/* Sets */}
                                  <div style={{ width: '80px' }} className="p-1 border-right-1 surface-border">
                                    <InputText
                                      className="w-full p-inputtext-sm text-center font-bold"
                                      value={exercise.sets || ''}
                                      onChange={(e) =>
                                        updateExerciseProperty(group.id, exercise.id, 'sets', e.target.value)
                                      }
                                      placeholder="3"
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={() => setIsEditing(false)}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      style={{
                                        borderRadius: 'var(--ios-radius-sm)'
                                      }}
                                    />
                                  </div>

                                  {/* Reps */}
                                  <div style={{ width: '80px' }} className="p-1 border-right-1 surface-border">
                                    <InputText
                                      className="w-full p-inputtext-sm text-center font-bold"
                                      value={exercise.repetitions || ''}
                                      onChange={(e) =>
                                        updateExerciseProperty(group.id, exercise.id, 'repetitions', e.target.value)
                                      }
                                      placeholder="10"
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={() => setIsEditing(false)}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      style={{
                                        borderRadius: 'var(--ios-radius-sm)'
                                      }}
                                    />
                                  </div>

                                  {/* Weight */}
                                  <div style={{ width: '80px' }} className="p-1 border-right-1 surface-border">
                                    <InputText
                                      className="w-full p-inputtext-sm text-center font-bold"
                                      value={exercise.weight || ''}
                                      onChange={(e) =>
                                        updateExerciseProperty(group.id, exercise.id, 'weight', e.target.value)
                                      }
                                      placeholder="10"
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={() => setIsEditing(false)}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      style={{
                                        borderRadius: 'var(--ios-radius-sm)'
                                      }}
                                    />
                                  </div>

                                  {/* Duration */}
                                  <div style={{ width: '80px' }} className="p-1 border-right-1 surface-border">
                                    <InputText
                                      className="w-full p-inputtext-sm text-center font-bold"
                                      value={exercise.duration || ''}
                                      onChange={(e) =>
                                        updateExerciseProperty(group.id, exercise.id, 'duration', e.target.value)
                                      }
                                      placeholder="10"
                                      onFocus={() => setIsEditing(true)}
                                      onBlur={() => setIsEditing(false)}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      style={{
                                        borderRadius: 'var(--ios-radius-sm)'
                                      }}
                                    />
                                  </div>

                                  {/* Options */}
                                  <div style={{ width: '60px' }} className="p-1 flex gap-1 justify-content-center">
                                    <Button
                                      icon="pi pi-cog"
                                      className="p-button-sm p-button-text p-button-rounded"
                                      onClick={() => handleOpenSetConfigDialog(group, exercise)}
                                      tooltip="Configurar sets"
                                      style={{
                                        width: '1.5rem',
                                        height: '1.5rem',
                                        color: '#4d7c0f'
                                      }}
                                    />
                                    <Button
                                      icon="pi pi-trash"
                                      className="p-button-sm p-button-text p-button-rounded"
                                      onClick={() => removeExerciseFromGroup(group.id, exercise.id)}
                                      tooltip="Eliminar"
                                      style={{
                                        width: '1.5rem',
                                        height: '1.5rem',
                                        color: '#ef4444'
                                      }}
                                    />
                                  </div>
                                </div>
                              </DragableRow>
                            );
                          })}
                        </SortableContext>

                        {/* Drop indicator at end of group */}
                        {activeId && activeId.startsWith('exercise-') && overId === `group-${group.id}` && (
                          <div
                            style={{
                              height: '2px',
                              background: '#a3e635',
                              margin: '2px 0',
                              borderRadius: '2px'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </DragableRow>
              ))}
            </SortableContext>

            {/* DragOverlay */}
            <DragOverlay>
              {activeId && activeGroup ? (
                <div
                  style={{
                    background: 'var(--ios-card-bg)',
                    borderRadius: 'var(--ios-radius-lg)',
                    boxShadow: 'var(--ios-shadow-xl)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="flex align-items-center font-bold p-3"
                    style={{
                      background: '#a3e635',
                      color: '#0a0a0a'
                    }}
                  >
                    <FaGripVertical className="mr-2" />
                    <span>
                      {activeGroup.data?.name ? activeGroup.data?.name : `Grupo ${activeGroup.data?.groupNumber}`}
                    </span>
                  </div>
                  {activeGroup.children?.map(
                    (
                      exercise: any // eslint-disable-line @typescript-eslint/no-explicit-any
                    ) => (
                      <div
                        key={exercise.key}
                        className="flex align-items-center gap-2 p-2"
                        style={{
                          borderBottom: '1px solid var(--ios-divider)',
                          fontSize: '0.85rem'
                        }}
                      >
                        <FaGripVertical
                          style={{
                            color: 'var(--ios-text-tertiary)',
                            fontSize: '0.7rem'
                          }}
                        />
                        <span className="flex-grow-1">{exercise.data?.name || 'Ejercicio'}</span>
                        <span style={{ color: 'var(--ios-text-secondary)' }}>
                          {exercise.sets || '-'} x {exercise.repetitions || '-'}
                        </span>
                      </div>
                    )
                  )}
                </div>
              ) : activeId && activeExercise ? (
                <div
                  className="flex align-items-center gap-2 p-3"
                  style={{
                    background: 'var(--ios-card-bg)',
                    borderRadius: 'var(--ios-radius-md)',
                    boxShadow: 'var(--ios-shadow-xl)',
                    fontSize: '0.85rem'
                  }}
                >
                  <FaGripVertical
                    style={{
                      color: 'var(--ios-text-tertiary)',
                      fontSize: '0.7rem'
                    }}
                  />
                  <span className="flex-grow-1">{activeExercise.exercise?.name || 'Ejercicio'}</span>
                  <span style={{ color: 'var(--ios-text-secondary)' }}>
                    {activeExercise.sets || '-'} x {activeExercise.repetitions || '-'}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      <SetConfigDialog
        visible={visible}
        onHide={closeDialog}
        selectedExercise={selectedExercise}
        selectedGroup={selectedGroup}
        toggleSetConfiguration={toggleSetConfiguration}
        updateSetConfiguration={updateSetConfiguration}
      />

      <ImportWorkoutAIDialog
        visible={importDialogVisible}
        onHide={() => setImportDialogVisible(false)}
        onImported={(result) => {
          // El nombre del plan/instance lo cargamos solo si esta vacio para
          // no pisar lo que el coach ya tipeo.
          const currentName = isTemplate ? plan.planName : plan.instanceName;
          const nextPlanName = isTemplate ? plan.planName : plan.instanceName;
          if (!currentName || currentName.trim().length === 0) {
            if (isTemplate) {
              updatePlanName(result.planName);
            } else {
              updateInstanceName(result.planName);
            }
          }
          setGroups(result.groups);

          // Auto-save: snapshot inmediato como draft IA. Si el usuario refresca
          // la pagina no pierde la llamada que ya hizo a OpenAI.
          if (!isEdit && !isViewOnlyMode) {
            const planNameForDraft =
              (nextPlanName && nextPlanName.trim().length > 0 ? nextPlanName : result.planName) ?? '';
            const id = saveDraftAction({
              kind: 'workout',
              planName: isTemplate ? planNameForDraft : '',
              instanceName: isTemplate ? '' : planNameForDraft,
              personalizedNotes: result.personalizedNotes,
              groups: result.groups,
              source: 'ai'
            });
            setCurrentDraftId(id);
            setDraftSource('ai');
            autoSaveEnabledRef.current = true;
          }
        }}
      />

      <QuickCreateExerciseDialog
        visible={quickCreate.visible}
        initialName={quickCreate.initialName}
        onHide={() => setQuickCreate((s) => ({ ...s, visible: false }))}
        onCreated={(ex) => {
          // Si el create salio de un dropdown puntual, auto-seleccionamos
          // ese ejercicio en la fila correspondiente.
          if (quickCreate.target) {
            updateExerciseSelection(quickCreate.target.groupId, quickCreate.target.exerciseId, ex.id);
          }
        }}
      />
    </>
  );
};
